<template>
  <nav
    v-if="isAuthenticated"
    ref="navEl"
    class="vpn-tab-nav"
    role="navigation"
    :aria-label="$t('nav.mainNavigation')"
  >
    <RouterLink
      v-for="(item, index) in items"
      :key="item.name"
      :ref="(el) => setTabRef(item.name, el)"
      :to="item.route"
      class="vpn-tab-nav__tab"
      :class="{
        'vpn-tab-nav__tab--active': isActive(item),
        'vpn-tab-nav__tab--overflow': index >= visibleCount,
      }"
      :aria-current="isActive(item) ? 'page' : undefined"
      :aria-hidden="index >= visibleCount ? 'true' : undefined"
      :tabindex="index >= visibleCount ? -1 : undefined"
    >
      {{ $t(item.label) }}
      <!-- Решение оператора 2026-09-18 — цветной значок у «Ключа», пока
           человек ни разу не подключался: полоска обязана показывать, куда
           нажимать. После первого подключения значок гаснет, пункт остаётся. -->
      <span
        v-if="item.name === 'connect' && neverConnected"
        class="vpn-tab-nav__dot"
        data-test="tab-nav-connect-dot"
        aria-hidden="true"
      />
    </RouterLink>

    <!-- На телефоне гамбургер есть всегда: помимо невлезших разделов в шторке
         живёт «Установить приложение». -->
    <button
      v-if="overflowItems.length > 0 || isMobile"
      class="vpn-tab-nav__more"
      :class="{ 'vpn-tab-nav__more--active': overflowItems.some(isActive) }"
      type="button"
      data-testid="tab-nav-menu"
      :aria-label="$t('nav.openMenu')"
      :aria-expanded="menuOpen ? 'true' : 'false'"
      @click="menuOpen = true"
    >
      <span class="vpn-tab-nav__more-icon" aria-hidden="true" />
    </button>

    <AppSidebar :open="menuOpen" :items="overflowItems" @close="menuOpen = false" />
  </nav>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'

import AppSidebar from '@/components/navigation/AppSidebar.vue'
import { BOTTOM_NAVIGATION_NAMES } from '@/shared/config/navigation'
import type { NavigationItem } from '@/shared/types/navigation'
import { useVisibleNavigation } from '@/shared/composables/useVisibleNavigation'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import { useVpnStore } from '@/modules/vpn/store/vpnStore'
import { cssIconUrl } from '@/shared/utils/assets'
import IconMenu from '@/assets/images/icons/sections/menu.svg'

const MOBILE_QUERY = '(max-width: 768px)'
// Ширина кнопки-гамбургера вместе с отступом до соседней вкладки — столько
// места резервируем, когда вкладки не влезают целиком (см. стили `&__more`).
const MORE_BUTTON_WIDTH = 40
const TAB_GAP = 4

const route = useRoute()
const menuIcon = cssIconUrl(IconMenu)
const profileStore = useProfileStore()
// Вкладки появляются сразу из кеша прошлого визита, не после ответа сети.
const { isAuthenticated } = storeToRefs(profileStore)
const { items: visibleItems } = useVisibleNavigation()

/**
 * «Ни разу не подключался» — то же правило, что у блока «Настроить VPN» на
 * «Главной»: другого признака нет, а отсутствующее ПОЛЕ читается как ноль.
 *
 * Но самого ОТВЕТА дождаться обязательно. Полоска живёт на всех экранах, и
 * далеко не каждый спрашивает обзор: после перезагрузки на «Гильдии» или
 * «Наградах» овервью нет вовсе, и значок горел бы у давно подключившегося
 * человека без всякой возможности его погасить. Сам пункт меню при молчащем
 * ответе остаётся на месте — это безопасное направление, а вот подсветка
 * «сделай дело» без факта дела не имеет права появляться.
 */
const vpnStore = useVpnStore()
const neverConnected = computed(
  () => !!vpnStore.overview && (vpnStore.overview.access?.devices_used ?? 0) === 0,
)

// На телефоне пять основных разделов живут в нижнем меню (AppBottomNav);
// наверху остаются остальные. Шире 768px нижнего меню нет — наверху всё.
const isMobile = ref(false)

const items = computed<NavigationItem[]>(() =>
  isMobile.value
    ? visibleItems.value.filter((item) => !BOTTOM_NAVIGATION_NAMES.includes(item.name))
    : visibleItems.value,
)

/**
 * Сколько вкладок с начала списка влезает в полоску. Что не влезло — уходит
 * в гамбургер, а не прячется за горизонтальным скроллом. Невлезшие вкладки
 * остаются в DOM невидимыми, чтобы их ширину можно было мерить при каждом
 * пересчёте.
 */
const visibleCount = ref(Number.POSITIVE_INFINITY)
const overflowItems = computed(() => items.value.slice(visibleCount.value))
const menuOpen = ref(false)

const navEl = ref<HTMLElement | null>(null)
const tabEls = new Map<string, HTMLElement>()

const setTabRef = (name: string, el: Element | ComponentPublicInstance | null) => {
  const element = el && '$el' in el ? (el.$el as HTMLElement) : (el as HTMLElement | null)
  if (element) tabEls.set(name, element)
  else tabEls.delete(name)
}

const measure = () => {
  const nav = navEl.value
  if (!nav) return

  const styles = window.getComputedStyle(nav)
  const available =
    nav.clientWidth - parseFloat(styles.paddingLeft || '0') - parseFloat(styles.paddingRight || '0')

  // Полоска ещё не разложена (или это jsdom) — показываем всё, прятать не за что.
  if (!(available > 0)) {
    visibleCount.value = items.value.length
    return
  }

  const widths = items.value.map((item) => tabEls.get(item.name)?.offsetWidth ?? 0)
  const total = widths.reduce((sum, width, index) => sum + width + (index ? TAB_GAP : 0), 0)
  // На телефоне место под гамбургер резервируем всегда — он там есть всегда.
  if (!isMobile.value && total <= available) {
    visibleCount.value = items.value.length
    return
  }

  const budget = available - MORE_BUTTON_WIDTH - TAB_GAP
  let used = 0
  let count = 0
  for (const width of widths) {
    const next = used + (count ? TAB_GAP : 0) + width
    if (next > budget) break
    used = next
    count += 1
  }
  visibleCount.value = count
}

const scheduleMeasure = () => nextTick(measure)

let mediaQuery: MediaQueryList | null = null
let resizeObserver: ResizeObserver | null = null

const onMediaChange = (event: MediaQueryListEvent) => {
  isMobile.value = event.matches
}

onMounted(() => {
  if (typeof window.matchMedia === 'function') {
    mediaQuery = window.matchMedia(MOBILE_QUERY)
    isMobile.value = mediaQuery.matches
    mediaQuery.addEventListener('change', onMediaChange)
  }

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(measure)
    if (navEl.value) resizeObserver.observe(navEl.value)
  }

  // Шрифт мог догрузиться после первого замера — ширины подписей изменятся.
  document.fonts?.ready.then(measure)
  scheduleMeasure()
})

onBeforeUnmount(() => {
  mediaQuery?.removeEventListener('change', onMediaChange)
  resizeObserver?.disconnect()
})

// Полоска появляется и исчезает вместе с авторизацией (v-if) — следим за
// актуальным элементом.
watch(navEl, (el) => {
  resizeObserver?.disconnect()
  if (el) resizeObserver?.observe(el)
})

watch(items, scheduleMeasure)

const isActive = (item: NavigationItem) => route.path.startsWith(item.route)
</script>

<style scoped lang="scss">
// Верхняя полоска разделов. Без горизонтального скролла: что не влезло по
// ширине, уходит под кнопку-гамбургер справа (AppSidebar). На телефоне пять
// основных разделов живут в нижнем меню и здесь не повторяются.
.vpn-tab-nav {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 16px;
  overflow: hidden;
  background: var(--ds-surface);
  border-bottom: 1px solid var(--ds-border);

  &__tab {
    flex: none;
    display: inline-flex;
    align-items: center;
    padding: 9px 14px;
    border-radius: var(--ds-radius-control);
    color: var(--ds-text-secondary);
    font-size: var(--ds-text-size-body);
    font-weight: var(--ds-weight-medium);
    text-decoration: none;
    white-space: nowrap;
    transition: background 0.15s ease, color 0.15s ease;
    -webkit-tap-highlight-color: transparent;

    // IGRA-32 — hover must not override the active tab (0,2,0 vs 0,1,0).
    @media (hover: hover) {
      &:hover:not(&--active) {
        background: var(--ds-accent-soft-08);
        color: var(--ds-text);
      }
    }
  }

  // Значок «сюда ещё не заходили» — тот же приём, что у счётчика колокольчика:
  // кружок акцентом. Пульсация тихая и выключается там, где человек просил
  // меньше движения.
  &__dot {
    flex: none;
    width: 8px;
    height: 8px;
    margin-left: 6px;
    border-radius: 999px;
    background: var(--ds-accent);
    animation: vpn-tab-nav-dot-pulse 1.8s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  &__tab--active {
    background: var(--ds-accent-soft-12);
    color: var(--ds-accent);
    font-weight: var(--ds-weight-semibold);

    @media (hover: hover) {
      &:hover {
        background: var(--ds-accent-soft-18);
      }
    }
  }

  // Невлезшая вкладка: не занимает места и не видна, но её ширину по-прежнему
  // можно измерить для следующего пересчёта.
  &__tab--overflow {
    position: absolute;
    visibility: hidden;
    pointer-events: none;
  }

  &__more {
    flex: none;
    margin-left: auto;
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: var(--ds-radius-control);
    background: transparent;
    color: var(--ds-text-secondary);
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    -webkit-tap-highlight-color: transparent;

    @media (hover: hover) {
      &:hover {
        background: var(--ds-accent-soft-08);
        color: var(--ds-text);
      }
    }
  }

  &__more-icon {
    width: 22px;
    height: 22px;
    background-color: currentColor;
    mask: v-bind(menuIcon) no-repeat center / contain;
    -webkit-mask: v-bind(menuIcon) no-repeat center / contain;
  }

  &__more--active {
    background: var(--ds-accent-soft-12);
    color: var(--ds-accent);
  }
}

@keyframes vpn-tab-nav-dot-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
</style>
